import {
  CheckCircle2,
  Circle,
  Clock,
  type LucideIcon,
  Pause,
  Send,
  XCircle,
} from "lucide-react";

import { cn } from "@/lib/utils";

/* Status pill — guide §5 + §17: status is conveyed by icon + text + color
   together (never color alone). Each tone bundles a default icon so callers
   that don't pass one still ship with the expected affordance. */

export type StatusTone =
  | "active"
  | "approved"
  | "success"
  | "pending"
  | "warning"
  | "rejected"
  | "danger"
  | "draft"
  | "neutral"
  | "info"
  | "accent";

const TONE_STYLES: Record<StatusTone, string> = {
  active:   "bg-[#ECFDF5] text-[#047857]",
  approved: "bg-[#ECFDF5] text-[#047857]",
  success:  "bg-[#ECFDF5] text-[#047857]",
  pending:  "bg-[#FEF3C7] text-[#92400E]",
  warning:  "bg-[#FEF3C7] text-[#92400E]",
  rejected: "bg-[#FEE2E2] text-[#B91C1C]",
  danger:   "bg-[#FEE2E2] text-[#B91C1C]",
  draft:    "bg-slate-100 text-slate-600",
  neutral:  "bg-slate-100 text-slate-600",
  info:     "bg-[#ECFEFF] text-[#0E7490]",
  accent:   "bg-[#F3E8FF] text-[#6B21A8]",
};

const TONE_DEFAULT_ICON: Record<StatusTone, LucideIcon> = {
  active:   CheckCircle2,
  approved: CheckCircle2,
  success:  CheckCircle2,
  pending:  Clock,
  warning:  Clock,
  rejected: XCircle,
  danger:   XCircle,
  draft:    Pause,
  neutral:  Circle,
  info:     Send,
  accent:   Circle,
};

export interface StatusPillProps {
  tone: StatusTone;
  children: React.ReactNode;
  icon?: LucideIcon;
  className?: string;
}

export function StatusPill({ tone, icon, children, className }: StatusPillProps) {
  // Tolerate an unknown/undefined tone (e.g. a backend enum value with no
  // mapping, or a null) — fall back to neutral rather than rendering an
  // undefined icon component, which would crash the whole page (React #130).
  const Icon = icon ?? TONE_DEFAULT_ICON[tone] ?? Circle;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium leading-none",
        TONE_STYLES[tone] ?? TONE_STYLES.neutral,
        className,
      )}
    >
      <Icon size={12} strokeWidth={2} aria-hidden="true" />
      <span>{children}</span>
    </span>
  );
}

/* Map an Employee.status string to the right tone. Centralised here so any
   list/detail screen renders the same pill everywhere. */
export function employeeStatusTone(status: string): StatusTone {
  switch (status) {
    case "active":
      return "active";
    case "on_leave":
      return "info";
    case "suspended":
    case "awol":
      return "warning";
    case "terminated":
      return "danger";
    case "resigned":
    case "retired":
    case "deceased":
      return "neutral";
    default:
      return "draft";
  }
}

/* Generic workflow-state tone — payroll runs, salary slips, expense claims,
   leave applications, job openings all share roughly the same vocabulary. */
export function workflowStatusTone(state: string): StatusTone {
  switch (state) {
    case "draft":
      return "draft";
    case "submitted":
    case "in_progress":
    case "computed":
    case "pending":
    case "open":
    case "screening":
    case "interview":
    case "interviewing":
    case "offer":
    case "offered":
      return "info";
    case "approved":
    case "active":
    case "running":
      return "approved";
    case "paid":
    case "reimbursed":
    case "completed":
    case "hired":
    case "filled":
    case "succeeded":
      return "success";
    case "rejected":
    case "cancelled":
    case "failed":
    case "withdrawn":
      return "danger";
    case "on_hold":
    case "needs_revision":
      return "warning";
    default:
      return "neutral";
  }
}

export function humanizeStatus(status: string): string {
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
